/**
 * Failure codes a chat client shows and reasons about. Codes only: the
 * sentences belong to each application, in its language.
 */
import { isObject } from './events.js';
export const REFUSAL_CODES = new Set([
    'request_already_admitted',
    'request_admission_unconfirmed',
    'request_payload_conflict',
    'admission_unavailable',
    'runner_updating',
    'busy',
    'harness_mismatch',
    'pointer_invalid',
    'harness_unavailable',
    'worker_unavailable',
    'unavailable',
    'invalid_request',
    'validation',
    'input_disabled',
    'model_harness_mismatch',
    'model_required',
    'unauthorized',
    'not_found',
    'runner_error',
]);
const RETRYABLE = new Set([
    'admission_unavailable',
    'runner_updating',
    'harness_unavailable',
    'worker_unavailable',
    'unavailable',
    'stream_lost',
    'network',
    'resume_failed',
]);
const RUNNER_ERROR_CODES = new Set([
    'runner_updating',
    'harness_mismatch',
    'pointer_invalid',
    'model_harness_mismatch',
    'model_required',
    'harness_unavailable',
    'busy',
    'input_disabled',
]);
export function isRetryable(code) {
    return RETRYABLE.has(code);
}
export function failureKind(code) {
    switch (code) {
        case 'busy':
            return 'busy';
        case 'request_already_admitted':
        case 'request_admission_unconfirmed':
        case 'request_payload_conflict':
        case 'harness_mismatch':
        case 'pointer_invalid':
        case 'model_harness_mismatch':
        case 'model_required':
        case 'input_disabled':
        case 'unauthorized':
        case 'not_found':
            return 'cannot-continue';
        case 'admission_unavailable':
        case 'runner_updating':
        case 'harness_unavailable':
        case 'worker_unavailable':
        case 'unavailable':
        case 'stream_lost':
        case 'network':
        case 'resume_failed':
            return 'retry-later';
        case 'invalid_request':
        case 'validation':
            return 'invalid';
        default:
            return 'failed';
    }
}
/**
 * Read an error body: the runner's FastAPI shape (`detail` as text, a coded
 * object, or a validation list) or an application's relay of it
 * (`error`/`code` and `detail`/`message` at the top level).
 */
export function refusalDetail(body) {
    let parsed = body;
    if (typeof body === 'string') {
        try {
            parsed = JSON.parse(body);
        }
        catch {
            parsed = null;
        }
    }
    if (!isObject(parsed))
        return { error: null, message: '', boundHarness: null };
    const detail = parsed.detail;
    let error = parsed.error ?? parsed.code;
    let bound = parsed.bound_harness ?? parsed.boundHarness;
    let message = '';
    if (isObject(detail)) {
        error = detail.error ?? error;
        bound = detail.bound_harness ?? bound;
        const text = detail.message ?? detail.detail;
        message = typeof text === 'string' ? text : '';
    }
    else if (typeof detail === 'string') {
        message = detail;
    }
    else if (Array.isArray(detail)) {
        message = detail
            .filter((item) => isObject(item) && typeof item.msg === 'string')
            .map((item) => {
            const where = (Array.isArray(item.loc) ? item.loc : []).filter((part) => part !== 'body').join('.');
            return where ? `${where}: ${item.msg}` : item.msg;
        })
            .join('; ');
    }
    if (!message && typeof parsed.message === 'string')
        message = parsed.message;
    return {
        error: typeof error === 'string' ? error : null,
        message,
        boundHarness: typeof bound === 'string' ? bound : null,
    };
}
/** The refusal code for a non-2xx response; `null` for a 2xx. */
export function refusalCode(status, body) {
    if (status >= 200 && status < 300)
        return null;
    const { error, message } = refusalDetail(body);
    if (error && REFUSAL_CODES.has(error) && (RUNNER_ERROR_CODES.has(error) || status >= 400)) {
        return error;
    }
    if (status === 400)
        return message.startsWith('Human input is not enabled') ? 'input_disabled' : 'invalid_request';
    if (status === 401 || status === 403)
        return 'unauthorized';
    if (status === 404)
        return 'not_found';
    if (status === 409)
        return 'busy';
    if (status === 422)
        return 'validation';
    if (status === 502)
        return 'worker_unavailable';
    if (status === 503 || status === 504)
        return 'unavailable';
    return 'runner_error';
}
/** Detail text of the pre-structured `busy` 409 (`{"detail": "Session is still running"}`). */
export const LEGACY_BUSY_DETAIL = 'Session is still running';
/**
 * True only when a 409 explicitly says `busy`: a structured `error: "busy"`
 * or the legacy plain-string detail. Unlike `refusalCode`, which shows any
 * other 409 as `busy` for display compatibility, this never treats an unknown
 * or future code, malformed JSON, or an empty body as retryable.
 */
export function isBusyRefusal(status, body) {
    if (status !== 409)
        return false;
    const { error, message } = refusalDetail(body);
    if (error !== null)
        return error === 'busy';
    return message.startsWith(LEGACY_BUSY_DETAIL);
}
export function chatError(code, message, boundHarness) {
    const error = { code, retryable: isRetryable(code) };
    if (message)
        error.message = message;
    if (boundHarness)
        error.boundHarness = boundHarness;
    return error;
}
/** The error a failed HTTP response means, for a send, a resume or an input answer. */
export function errorFromResponse(status, body) {
    const code = refusalCode(status, body);
    if (!code)
        return null;
    const { message, boundHarness } = refusalDetail(body);
    return chatError(code, message, boundHarness);
}
/** Reason on the `error` event a replay emits when `after_uuid` names no event the runner still holds. */
export const CURSOR_UNKNOWN_REASON = 'cursor_unknown';
/**
 * The error a stream `error` event means. `cursor_unknown` is a failed
 * resume, not a failed turn: drop the cursor and reload the transcript page
 * (see `isCursorUnknown`); never resend the message.
 */
export function errorFromStreamReason(reason, message) {
    const code = reason === 'harness_mismatch'
        ? 'harness_mismatch'
        : reason === 'resume_failed' || reason === CURSOR_UNKNOWN_REASON
            ? 'resume_failed'
            : 'turn_failed';
    return chatError(code, typeof message === 'string' ? message : undefined);
}
/** True for the replay `error` event that asks the client to forget its cursor and reload history. */
export function isCursorUnknown(event) {
    return (typeof event === 'object' &&
        event !== null &&
        event.type === 'error' &&
        event.reason === CURSOR_UNKNOWN_REASON);
}
